import { Button } from 'components/Button/Button'
import PrerequisitesSettingsForm from 'components/PrerequisitesSettings/PrerequisitesSettingsForm'
import Title from 'components/Title/Title'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import type { AutonomicsResourceProfile } from 'utils/interfaces'
import { OdysseyStartSettings, mergeOdysseySettings } from 'utils/odysseySettings'

import style from './PrerequisitesSettingsModal.module.css'

type Props = {
  isOpen: boolean
  value: OdysseyStartSettings
  profile?: AutonomicsResourceProfile | null
  hideDirtyWater?: boolean
  /** Called with confirmed settings; parent persists / keeps in task form state. */
  onSave: (next: OdysseyStartSettings) => void | Promise<void>
  onClose: () => void
}

const PrerequisitesSettingsModal: React.FC<Props> = ({
  isOpen,
  value,
  profile,
  hideDirtyWater = false,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<OdysseyStartSettings>(() => mergeOdysseySettings(value, profile))
  const [saving, setSaving] = useState(false)
  const prevOpen = useRef(false)

  useEffect(() => {
    if (isOpen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      setDraft(mergeOdysseySettings(value, profile))
      setSaving(false)
    }
    prevOpen.current = isOpen
  }, [isOpen, value, profile])

  const onCancel = () => {
    setDraft(mergeOdysseySettings(value, profile))
    onClose()
  }

  const onConfirm = async () => {
    setSaving(true)
    try {
      await onSave(mergeOdysseySettings(draft, profile))
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      className={style.root}
      onRequestClose={onCancel}
      style={{
        overlay: {
          zIndex: 110,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.modal}>
        <div className={style.head}>
          <Title text={t('prerequisites.title')} className={style.title} />
        </div>

        <div className={style.content}>
          <PrerequisitesSettingsForm
            value={draft}
            profile={profile}
            hideDirtyWater={hideDirtyWater}
            disabled={saving}
            onChange={setDraft}
          />
        </div>

        <div className={style.footer}>
          <Button.Outlined className={style.btn} onClick={onCancel} disabled={saving}>
            {t('mission_settings.label_cancel')}
          </Button.Outlined>
          <Button.Standart className={style.btn} onClick={onConfirm} loading={saving} disabled={saving}>
            {t('mission_settings.label_save')}
          </Button.Standart>
        </div>
      </div>
    </Modal>
  )
}

export default PrerequisitesSettingsModal
